import {makeAutoObservable, runInAction} from 'mobx';
import {PiggyBank, ShieldCheck} from 'lucide-react';
import type {GetUserRepScoreUseCase} from '../../core/application/use-cases/repScore/GetUserRepScoreUseCase';
import type {
    RepScoreService,
    RepScoreResponse,
} from '../../core/infrastructure/services/RepScoreService';
import type {
    TrustTier,
    RepScoreViewState,
    TierConfig,
    JourneyStep,
} from '../../shared/types/RepScoreTypes';
import {
    TIER_CONFIGS,
    AT_RISK_CONFIG,
    getTierFromScoreLevel,
    getTierFromScore,
} from '../../components/drawers/repScore/constants';
import {
    MOCK_ENABLED,
    getMockRepScore,
} from './__mocks__/repScoreMockData';

export class RepScoreStore {
    // State
    repScore: RepScoreResponse | null = null;
    isLoading = false;
    error: string | null = null;

    constructor(
        private readonly getUserRepScoreUseCase: GetUserRepScoreUseCase,
        private readonly repScoreService: RepScoreService
    ) {
        makeAutoObservable(this);

        // Initialize with mock data if enabled
        if (MOCK_ENABLED) {
            this.repScore = getMockRepScore();
        }
    }

    // =========================================================================
    // Core Computed Values
    // =========================================================================

    get hasRepScore(): boolean {
        return this.repScore !== null;
    }

    get isPSSEligible(): boolean {
        return this.repScore?.pssEligible || false;
    }

    get currentScore(): number {
        return this.repScore?.currentScore || 0;
    }

    get scoreLevel(): string {
        return this.repScore?.scoreLevel || 'unknown';
    }

    get maxPSSAmount(): string {
        return this.repScore?.maxPssAmount || '0';
    }

    get pssCreditPercentage(): string {
        return this.repScore?.pssCreditPercentage || '0';
    }

    get nextLevelThreshold(): number {
        return this.repScore?.nextLevelThreshold || 0;
    }

    get progressToNextLevel(): number {
        if (!this.repScore) return 0;
        const current = this.repScore.currentScore;
        const next = this.repScore.nextLevelThreshold;
        const previous = this.getPreviousLevelThreshold();

        if (next === current) return 100;
        return ((current - previous) / (next - previous)) * 100;
    }

    get scoreBreakdown() {
        return this.repScore?.scoreBreakdown || null;
    }

    get recentScoreChanges() {
        return this.repScore?.scoreHistory?.slice(0, 5) || [];
    }

    get earlyPickupStatus() {
        return this.repScore?.earlyPickupStatus;
    }

    /**
     * Whether the user has any meaningful repayment or order history.
     *
     * This is intentionally broader than on-time payments. A customer with
     * missed or late payments is not "new", even if their current score is low.
     */
    get hasMeaningfulPaymentHistory(): boolean {
        if (this.earlyPickupStatus) {
            return this.earlyPickupStatus.hasMeaningfulPaymentHistory;
        }

        const paymentHistory = this.scoreBreakdown?.paymentHistory;
        const purchaseBehavior = this.scoreBreakdown?.purchaseBehavior;

        return (
            (paymentHistory?.totalPayments ?? 0) > 0 ||
            (paymentHistory?.onTimePayments ?? 0) > 0 ||
            (paymentHistory?.latePayments ?? 0) > 0 ||
            (paymentHistory?.missedPayments ?? 0) > 0 ||
            (purchaseBehavior?.totalOrders ?? 0) > 0
        );
    }

    // =========================================================================
    // RepScore Drawer Computed Properties
    // =========================================================================

    /**
     * Get the current trust tier based on score level from backend.
     * Falls back to score-based calculation if backend tier is unknown.
     */
    get trustTier(): TrustTier {
        const backendTier = this.repScore?.scoreLevel;
        if (backendTier) {
            const tier = getTierFromScoreLevel(backendTier);
            if (tier !== 'starter' || backendTier.toLowerCase() === 'starter') {
                return tier;
            }
        }
        // Fallback to score-based tier
        return getTierFromScore(this.currentScore);
    }

    /**
     * Get the configuration for the current trust tier.
     */
    get tierConfig(): TierConfig {
        return TIER_CONFIGS[this.trustTier];
    }

    /**
     * Determine which view state to render in the drawer.
     */
    get viewState(): RepScoreViewState {
        // Check for at-risk state first (late payments in grace period)
        if (this.isInGracePeriod) {
            return 'at_risk';
        }

        if (this.earlyPickupStatus) {
            if (!this.earlyPickupStatus.hasMeaningfulPaymentHistory) {
                return 'new_user';
            }

            if (
                this.earlyPickupStatus.hasActiveEarlyPickupPlan ||
                this.earlyPickupStatus.eligibleForFuturePlans
            ) {
                return 'high_trust';
            }

            return 'progressing';
        }

        const score = this.currentScore;
        if (score < 250 && !this.hasMeaningfulPaymentHistory) return 'new_user';
        // Without the backend eligibility contract, fall back conservatively.
        // A high score alone does not prove Early Pickup is open on future plans.
        return 'progressing';
    }

    /**
     * Points remaining to reach the next tier.
     */
    get pointsToNextTier(): number {
        const nextTierScore = this.tierConfig.nextTierScore;
        if (nextTierScore === null) return 0; // Already at max tier
        return Math.max(0, nextTierScore - this.currentScore);
    }

    /**
     * Progress percentage within the current tier.
     */
    get progressToNextTierPercent(): number {
        const { min, max } = this.tierConfig.scoreRange;
        const range = max - min + 1;
        const progress = this.currentScore - min;
        return Math.min(100, Math.round((progress / range) * 100));
    }

    /**
     * Current early pickup threshold (percentage of plan paid to collect).
     */
    get earlyPickupThreshold(): number {
        return this.tierConfig.pickupThreshold;
    }

    /**
     * Current credit percentage benefit.
     */
    get creditPercentage(): number {
        return this.tierConfig.creditPercentage;
    }

    /**
     * Check if user has a late payment within the grace period.
     *
     * Detection: Check scoreHistory for recent "late" trigger within grace window.
     * Backend populates history from RepScoreHistory model with proper triggers.
     */
    get isInGracePeriod(): boolean {
        const stats = this.repScore?.scoreBreakdown?.paymentHistory;
        if (!stats || stats.latePayments === 0) return false;

        const history = this.recentScoreChanges;
        const recentLatePayment = history.find(
            (h) =>
                h.trigger?.toLowerCase().includes('late') &&
                this.isWithinGracePeriodWindow(h.date)
        );
        return !!recentLatePayment;
    }

    /**
     * Onboarding journey steps for new users.
     */
    get journeySteps(): JourneyStep[] {
        const completedPlans =
            this.scoreBreakdown?.purchaseBehavior?.totalOrders ?? 0;
        const hasVerifiedId =
            (this.scoreBreakdown?.profileCompletion?.completionPercentage ?? 0) ===
            100;

        return [
            {
                id: 'first_payment',
                title: 'Pay your first plan on time',
                description: 'Complete your first payment to start building trust',
                points: 50,
                completed: completedPlans > 0,
                icon: PiggyBank,
            },
            {
                id: 'verify_id',
                title: 'Verify your ID',
                description: 'Complete identity verification for bonus points',
                points: 20,
                completed: hasVerifiedId,
                icon: ShieldCheck,
            },
        ];
    }

    /**
     * Check if the benefit is currently locked (starter tier).
     */
    get isBenefitLocked(): boolean {
        return this.tierConfig.isLocked;
    }

    /**
     * Get the next tier info for progress display.
     */
    get nextTierInfo(): { name: string; threshold: number } | null {
        const nextTierScore = this.tierConfig.nextTierScore;
        if (nextTierScore === null) return null;

        // Find the next tier config
        const nextTier = getTierFromScore(nextTierScore);
        const nextConfig = TIER_CONFIGS[nextTier];
        return {
            name: nextConfig.displayName,
            threshold: nextTierScore,
        };
    }

    // =========================================================================
    // Private Helpers
    // =========================================================================

    /**
     * Check if a date is within the grace period window.
     */
    private isWithinGracePeriodWindow(dateStr: string): boolean {
        if (!dateStr) return false;
        try {
            const paymentDate = new Date(dateStr);
            const now = new Date();
            const hoursSince =
                (now.getTime() - paymentDate.getTime()) / (1000 * 60 * 60);
            return hoursSince <= AT_RISK_CONFIG.gracePeriodHours;
        } catch {
            return false;
        }
    }

    private getPreviousLevelThreshold(): number {
        if (!this.repScore) return 0;

        const current = this.repScore.currentScore;
        if (current >= 800) return 700;
        if (current >= 700) return 600;
        if (current >= 600) return 500;
        if (current >= 500) return 400;
        return 0;
    }

    private setLoading(loading: boolean): void {
        this.isLoading = loading;
    }

    private setError(error: string | null): void {
        this.error = error;
    }

    // =========================================================================
    // Actions
    // =========================================================================

    async loadUserRepScore(userId: string): Promise<void> {
        // Skip API call if using mock data
        if (MOCK_ENABLED) {
            return;
        }

        this.setLoading(true);
        this.setError(null);

        try {
            const repScore = await this.getUserRepScoreUseCase.execute({userId});

            runInAction(() => {
                this.repScore = repScore;
            });
        } catch (error) {
            this.setError(error instanceof Error ? error.message : 'Failed to load RepScore');
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    async refreshRepScore(userId: string): Promise<void> {
        await this.loadUserRepScore(userId);
    }

    // =========================================================================
    // Legacy Helper Methods
    // =========================================================================

    getScoreColor(): string {
        if (!this.repScore) return '#gray';

        const score = this.repScore.currentScore;
        if (score >= 800) return '#22c55e'; // green
        if (score >= 700) return '#3b82f6'; // blue
        if (score >= 600) return '#f59e0b'; // yellow
        if (score >= 500) return '#f97316'; // orange
        return '#ef4444'; // red
    }

    getScoreGrade(): string {
        if (!this.repScore) return 'N/A';

        const score = this.repScore.currentScore;
        if (score >= 800) return 'Excellent';
        if (score >= 700) return 'Good';
        if (score >= 600) return 'Fair';
        if (score >= 500) return 'Poor';
        return 'Very Poor';
    }

    canImproveScore(): boolean {
        if (!this.repScore) return false;

        const breakdown = this.repScore.scoreBreakdown;
        return (
            breakdown.profileCompletion.completionPercentage < 100 ||
            breakdown.profileCompletion.missingVerifications.length > 0
        );
    }

    getImprovementSuggestions(): string[] {
        if (!this.repScore) return [];

        const suggestions: string[] = [];
        const breakdown = this.repScore.scoreBreakdown;

        if (breakdown.profileCompletion.completionPercentage < 100) {
            suggestions.push('Complete your profile information');
        }

        if (breakdown.profileCompletion.missingVerifications.length > 0) {
            suggestions.push(`Complete ${breakdown.profileCompletion.missingVerifications.join(', ')} verification`);
        }

        if (breakdown.paymentHistory.successRate < 95) {
            suggestions.push('Maintain consistent payment history');
        }

        if (breakdown.purchaseBehavior.totalOrders < 5) {
            suggestions.push('Build purchase history with more orders');
        }

        return suggestions;
    }
}
